import React, { useState, useEffect, useRef } from 'react';
import { searchFacilitiesForLink } from '../api';

// Debounce helper
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * FacilitySearchSelect
 *
 * Props:
 *   onSelect(facility)  — called when a facility is selected
 *   selected            — currently selected facility object (or null)
 *   disabled            — disable the input
 */
export default function FacilitySearchSelect({ onSelect, selected, disabled, placeholder = 'Search by agency name…' }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const debouncedQuery        = useDebounce(query, 300);
  const containerRef          = useRef(null);

  // Search when query changes
  useEffect(() => {
    if (!debouncedQuery.trim() || debouncedQuery.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    searchFacilitiesForLink(debouncedQuery.trim())
      .then((data) => {
        setResults(data.slice(0, 10)); // cap at 10 results
        setOpen(true);
      })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [debouncedQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (facility) => {
    onSelect(facility);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const handleClear = () => {
    onSelect(null);
    setQuery('');
  };

  // If a facility is already selected, show the selected card
  if (selected) {
    return (
      <div className="flex items-start justify-between gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-blue-900 truncate">{selected.agency_name}</p>
          <p className="text-xs text-blue-700 mt-0.5">
            {[selected.physical_city, selected.physical_county].filter(Boolean).join(', ')}
          </p>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-800 underline mt-0.5"
          >
            Change
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          disabled={disabled}
          placeholder={placeholder}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400"
        />
        {loading && (
          <svg className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin w-4 h-4 text-gray-400"
            fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {results.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => handleSelect(f)}
              className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
            >
              <p className="text-sm font-medium text-gray-900 truncate">{f.agency_name}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {[f.physical_city, f.physical_county].filter(Boolean).join(', ')}
              </p>
            </button>
          ))}
        </div>
      )}

      {open && !loading && query.trim().length >= 2 && results.length === 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
          <p className="text-sm text-gray-400">No facilities found matching "{query}"</p>
        </div>
      )}
    </div>
  );
}
